// convex/lib/config/domains/stock.ts

import { validatorStockStatus } from "../../../companies/stock/status";
import { stockAlertTypeRule } from "../../../companies/stock/status/filter_rules";
import { validatorStockMovement } from "../../../companies/stock/movements";
import { stockMovementTypeRule } from "../../../companies/stock/movements/filter_rules";
import { stockConfigurationValidator } from "../../../companies/stock/configuration";
import { validatorProductService } from "../../../companies/products";
import { validatorBranch } from "../../../companies/branches";
import { validatorUsers } from "../../../system/users";
import { validatorPurchaseOrder } from "../../../companies/purchase_orders";
import { validatorPurchaseOrderItem } from "../../../companies/purchase_orders/items";
import { purchaseOrderStatusRule } from "../../../companies/purchase_orders/filter_rules";
import { validatorSupplier } from "../../../companies/suppliers";
import { validatorStockTransfer } from "../../../companies/stock_transfers";
import { validatorStockTransferItem } from "../../../companies/stock_transfers/items";
import { stockTransferStatusRule } from "../../../companies/stock_transfers/filter_rules";
import { validatorWarehouse } from "../../../companies/warehouses";
import { validatorInventoryCount } from "../../../companies/stock/counts";
import { validatorInventoryCountItem } from "../../../companies/stock/counts/items";
import { inventoryCountStatusRule } from "../../../companies/stock/counts/filter_rules";
import type { DomainGroup, TableScope } from "../types";

// ============================================================================
// STOCK DOMAIN - Status Values for i18n
// ============================================================================

export const STOCK_MOVEMENT_TYPES = [
  "entry",
  "exit",
  "adjustment",
  "transfer",
] as const;
export const STOCK_MOVEMENT_STATUSES = [
  "pending",
  "confirmed",
  "cancelled",
] as const;
export const PURCHASE_ORDER_STATUSES = [
  "draft",
  "sent",
  "confirmed",
  "partial",
  "received",
  "cancelled",
] as const;
export const STOCK_TRANSFER_STATUSES = [
  "draft",
  "in_transit",
  "partial",
  "received",
  "cancelled",
] as const;
export const INVENTORY_COUNT_STATUSES = [
  "draft",
  "completed",
  "cancelled",
] as const;

export const stockDomain: DomainGroup = {
  id: "stock",
  name: "Inventario",
  description: "Gestión de inventario y movimientos de stock",
  icon: "Package",
  order: 4,
  status: "active",
  // Acceso por defecto de company_user al grupo (el módulo `stock` lo baja a read)
  defaultAccess: { company_user: "full" },

  // ── Configuración i18n del dominio ────────────────────
  i18n: {
    namespace: "stock",
    keys: {
      title: "stock.title",
      description: "stock.description",
    },
  },

  modules: [
    {
      id: "stock",
      name: "Estado de Stock",
      dashboardAccess: "elevated",
      description: "Seguimiento de existencias y movimientos de stock",
      icon: "Package",
      permissions: ["canCreate", "canRead", "canUpdate", "canDelete"],
      // company_user solo lee inventario (override del "full" del grupo)
      defaultAccess: { company_user: "read" },
      routes: ["/inventory"],
      navigation: {
        showInNav: true,
        contexts: ["main"],
        navOrder: 1,
        status: "active",
      },

      // ── Configuración i18n del módulo ────────────────────
      i18n: {
        namespace: "stock.stock",
        keys: {
          title: "stock.stock.title",
          description: "stock.stock.description",
          longDescription: "stock.stock.longDescription",
          searchPlaceholder: "stock.stock.searchPlaceholder",
          emptyState: "stock.stock.emptyState",
          actions: {
            create: "stock.stock.actions.create",
            edit: "stock.stock.actions.edit",
            delete: "stock.stock.actions.delete",
            view: "stock.stock.actions.view",
          },
        },
        translatableFields: [],
        translatableEnums: [],
      },

      tables: [
        {
          tableName: "stock_status",
          schema: validatorStockStatus,
          scope: "branch" as TableScope,
          relations: [
            {
              key: "product",
              sourceField: "productId",
              schema: validatorProductService,
              tableName: "products",
              searchableFields: ["name", "code", "barcode", "model"],
            },
            {
              key: "warehouse",
              sourceField: "warehouseId",
              schema: validatorWarehouse,
              tableName: "warehouses",
            },
            {
              key: "lastUpdatedBy",
              sourceField: "internalControl.updatedBy",
              schema: validatorUsers,
              tableName: "users",
            },
            {
              key: "lastCreatedBy",
              sourceField: "internalControl.createdBy",
              schema: validatorUsers,
              tableName: "users",
            },
          ],
          searchFields: [],
          hasStatusIndex: false,
          customRules: [stockAlertTypeRule],
        },
      ],
      readOnlyTables: [{ tableName: "products" }, { tableName: "branches" }],
    },
    {
      id: "stock_configurations",
      name: "Inventario",
      description: "Configuración de inventario, valorización y reglas de stock",
      icon: "Settings2",
      permissions: ["canCreate", "canRead", "canUpdate", "canDelete"],
      defaultAccess: { company_admin: "full", company_user: "read" },
      routes: ["/settings/inventory"],
      navigation: {
        showInNav: true,
        contexts: ["settings"],
        navOrder: 7,
        status: "active",
      },
      i18n: {
        namespace: "stock.configuration",
        keys: {
          title: "stock.configuration.title",
          description: "stock.configuration.description",
          longDescription: "stock.configuration.longDescription",
          searchPlaceholder: "stock.configuration.searchPlaceholder",
          emptyState: "stock.configuration.emptyState",
          actions: {
            create: "stock.configuration.actions.create",
            edit: "stock.configuration.actions.edit",
            delete: "stock.configuration.actions.delete",
            view: "stock.configuration.actions.view",
          },
        },
        translatableFields: [],
        translatableEnums: [],
      },
      tables: [
        {
          tableName: "stock_configurations",
          schema: stockConfigurationValidator,
          scope: "branch" as TableScope,
          relations: [
            {
              key: "branch",
              sourceField: "branchId",
              schema: validatorBranch,
              tableName: "branches",
            },
          ],
          searchFields: ["productId"],
          hasStatusIndex: false,
          customRules: [stockAlertTypeRule],
        },
      ],
      readOnlyTables: [{ tableName: "branches" }],
    },
    {
      id: "stock_movements",
      name: "Movimientos",
      description: "Historial de entradas, salidas y ajustes de stock",
      icon: "ArrowLeftRight",
      permissions: ["canCreate", "canRead", "canUpdate", "canDelete"],
      defaultAccess: { company_user: "read" },
      routes: ["/inventory/movements"],
      navigation: {
        showInNav: true,
        contexts: ["main"],
        navOrder: 2,
        status: "active",
      },

      i18n: {
        namespace: "stock.movements",
        keys: {
          title: "stock.movements.title",
          description: "stock.movements.description",
          longDescription: "stock.movements.longDescription",
          searchPlaceholder: "stock.movements.searchPlaceholder",
          emptyState: "stock.movements.emptyState",
          actions: {
            create: "stock.movements.actions.create",
            edit: "stock.movements.actions.edit",
            delete: "stock.movements.actions.delete",
            view: "stock.movements.actions.view",
          },
        },
        translatableFields: [],
        translatableEnums: [
          { field: "type", values: STOCK_MOVEMENT_TYPES },
          { field: "status", values: STOCK_MOVEMENT_STATUSES },
        ],
      },

      tables: [
        {
          tableName: "stock_movements",
          schema: validatorStockMovement,
          scope: "branch" as TableScope,
          relations: [
            {
              key: "product",
              sourceField: "productId",
              schema: validatorProductService,
              tableName: "products",
              searchableFields: ["name", "code", "barcode", "model"],
            },
            {
              key: "warehouse",
              sourceField: "warehouseId",
              schema: validatorWarehouse,
              tableName: "warehouses",
            },
            {
              key: "lastUpdatedBy",
              sourceField: "internalControl.updatedBy",
              schema: validatorUsers,
              tableName: "users",
            },
            {
              key: "lastCreatedBy",
              sourceField: "internalControl.createdBy",
              schema: validatorUsers,
              tableName: "users",
            },
          ],
          searchFields: [
            "movementType",
            "reason",
            "referenceType",
            "referenceId",
            "batchNumber",
            "notes",
          ],
          hasStatusIndex: false,
          customRules: [stockMovementTypeRule],
        },
      ],
      readOnlyTables: [{ tableName: "products" }],
    },
    {
      id: "purchase_orders",
      name: "Órdenes de compra",
      description: "Crear, enviar y recibir órdenes de compra",
      icon: "ShoppingCart",
      dashboardAccess: "elevated",
      permissions: ["canCreate", "canRead", "canUpdate", "canDelete"],
      // Admin: full. Usuario común: solo lectura (override del grupo).
      defaultAccess: { company_admin: "full", company_user: "read" },
      routes: ["/purchase-orders"],
      navigation: {
        showInNav: true,
        contexts: ["main"],
        navOrder: 3,
        status: "active",
      },

      i18n: {
        namespace: "purchasing.orders",
        keys: {
          title: "purchasing.orders.title",
          description: "purchasing.orders.description",
          longDescription: "purchasing.orders.longDescription",
          searchPlaceholder: "purchasing.orders.searchPlaceholder",
          emptyState: "purchasing.orders.emptyState",
          actions: {
            create: "purchasing.orders.actions.create",
            edit: "purchasing.orders.actions.edit",
            delete: "purchasing.orders.actions.delete",
            view: "purchasing.orders.actions.view",
          },
        },
        translatableFields: [],
        translatableEnums: [
          { field: "status", values: PURCHASE_ORDER_STATUSES },
        ],
      },

      tables: [
        {
          tableName: "purchase_orders",
          schema: validatorPurchaseOrder,
          scope: "branch" as TableScope,
          relations: [
            {
              key: "supplier",
              sourceField: "supplierId",
              schema: validatorSupplier,
              tableName: "suppliers",
              searchableFields: [
                "companyName",
                "contactPerson",
                "taxId",
                "email",
              ],
            },
            {
              key: "lastUpdatedBy",
              sourceField: "internalControl.updatedBy",
              schema: validatorUsers,
              tableName: "users",
            },
            {
              key: "lastCreatedBy",
              sourceField: "internalControl.createdBy",
              schema: validatorUsers,
              tableName: "users",
            },
          ],
          searchFields: ["code", "notes", "paymentTerms"],
          hasStatusIndex: true,
          customRules: [purchaseOrderStatusRule],
        },
        {
          tableName: "purchase_orders_items",
          schema: validatorPurchaseOrderItem,
          scope: "company" as TableScope,
          relations: [
            {
              key: "purchaseOrder",
              schema: validatorPurchaseOrder,
              tableName: "purchase_orders",
            },
            {
              key: "product",
              schema: validatorProductService,
              tableName: "products",
            },
          ],
          searchFields: ["name", "description", "productCode"],
          hasStatusIndex: false,
          customRules: [],
        },
      ],
      readOnlyTables: [
        { tableName: "suppliers" },
        { tableName: "products" },
      ],
    },
    {
      id: "stock_transfers",
      name: "Transferencias",
      description: "Transferencias de inventario entre sucursales",
      icon: "ArrowLeftRight",
      dashboardAccess: "elevated",
      permissions: ["canCreate", "canRead", "canUpdate", "canDelete"],
      defaultAccess: { company_admin: "full", company_user: "read" },
      routes: ["/stock-transfers"],
      navigation: {
        showInNav: true,
        contexts: ["main"],
        navOrder: 4,
        status: "active",
      },

      i18n: {
        namespace: "stock.transfers",
        keys: {
          title: "stock.transfers.title",
          description: "stock.transfers.description",
          longDescription: "stock.transfers.longDescription",
          searchPlaceholder: "stock.transfers.searchPlaceholder",
          emptyState: "stock.transfers.emptyState",
          actions: {
            create: "stock.transfers.actions.create",
            edit: "stock.transfers.actions.edit",
            delete: "stock.transfers.actions.delete",
            view: "stock.transfers.actions.view",
          },
        },
        translatableFields: [],
        translatableEnums: [
          { field: "status", values: STOCK_TRANSFER_STATUSES },
        ],
      },

      tables: [
        {
          tableName: "stock_transfers",
          schema: validatorStockTransfer,
          scope: "company" as TableScope,
          relations: [
            {
              key: "fromWarehouse",
              sourceField: "fromWarehouseId",
              schema: validatorWarehouse,
              tableName: "warehouses",
            },
            {
              key: "toWarehouse",
              sourceField: "toWarehouseId",
              schema: validatorWarehouse,
              tableName: "warehouses",
            },
            {
              key: "lastUpdatedBy",
              sourceField: "internalControl.updatedBy",
              schema: validatorUsers,
              tableName: "users",
            },
            {
              key: "lastCreatedBy",
              sourceField: "internalControl.createdBy",
              schema: validatorUsers,
              tableName: "users",
            },
          ],
          searchFields: ["code", "notes"],
          hasStatusIndex: true,
          customRules: [stockTransferStatusRule],
        },
        {
          tableName: "stock_transfers_items",
          schema: validatorStockTransferItem,
          scope: "company" as TableScope,
          relations: [
            {
              key: "transfer",
              schema: validatorStockTransfer,
              tableName: "stock_transfers",
            },
            {
              key: "product",
              schema: validatorProductService,
              tableName: "products",
            },
          ],
          searchFields: ["name", "productCode"],
          hasStatusIndex: false,
          customRules: [],
        },
      ],
      readOnlyTables: [
        { tableName: "branches" },
        { tableName: "products" },
      ],
    },
    {
      id: "inventory_counts",
      name: "Conteos físicos",
      description: "Conteo físico de inventario y reconciliación",
      icon: "ListChecks",
      dashboardAccess: "elevated",
      permissions: ["canCreate", "canRead", "canUpdate", "canDelete"],
      defaultAccess: { company_admin: "full", company_user: "read" },
      routes: ["/inventory-counts"],
      navigation: {
        showInNav: true,
        contexts: ["main"],
        navOrder: 5,
        status: "active",
      },
      i18n: {
        namespace: "stock.counts",
        keys: {
          title: "stock.counts.title",
          description: "stock.counts.description",
          longDescription: "stock.counts.longDescription",
          searchPlaceholder: "stock.counts.searchPlaceholder",
          emptyState: "stock.counts.emptyState",
          actions: {
            create: "stock.counts.actions.create",
            edit: "stock.counts.actions.edit",
            delete: "stock.counts.actions.delete",
            view: "stock.counts.actions.view",
          },
        },
        translatableFields: [],
        translatableEnums: [
          { field: "status", values: INVENTORY_COUNT_STATUSES },
        ],
      },
      tables: [
        {
          tableName: "inventory_counts",
          schema: validatorInventoryCount,
          scope: "company" as TableScope,
          relations: [
            {
              key: "warehouse",
              sourceField: "warehouseId",
              schema: validatorWarehouse,
              tableName: "warehouses",
            },
            {
              key: "lastUpdatedBy",
              sourceField: "internalControl.updatedBy",
              schema: validatorUsers,
              tableName: "users",
            },
            {
              key: "lastCreatedBy",
              sourceField: "internalControl.createdBy",
              schema: validatorUsers,
              tableName: "users",
            },
          ],
          searchFields: ["code", "notes"],
          hasStatusIndex: true,
          customRules: [inventoryCountStatusRule],
        },
        {
          tableName: "inventory_counts_items",
          schema: validatorInventoryCountItem,
          scope: "company" as TableScope,
          relations: [
            {
              key: "product",
              schema: validatorProductService,
              tableName: "products",
            },
          ],
          searchFields: ["name", "productCode"],
          hasStatusIndex: false,
          customRules: [],
        },
      ],
      readOnlyTables: [
        { tableName: "warehouses" },
        { tableName: "products" },
      ],
    },
    {
      id: "warehouses",
      name: "Bodegas",
      description: "Almacenes por sucursal",
      icon: "Warehouse",
      permissions: ["canCreate", "canRead", "canUpdate", "canDelete"],
      defaultAccess: { company_admin: "full", company_user: "read" },
      routes: ["/settings/warehouses"],
      navigation: {
        showInNav: true,
        contexts: ["settings"],
        navOrder: 6,
        status: "active",
      },
      i18n: {
        namespace: "stock.warehouses",
        keys: {
          title: "stock.warehouses.title",
          description: "stock.warehouses.description",
          longDescription: "stock.warehouses.longDescription",
          searchPlaceholder: "stock.warehouses.searchPlaceholder",
          emptyState: "stock.warehouses.emptyState",
          actions: {
            create: "stock.warehouses.actions.create",
            edit: "stock.warehouses.actions.edit",
            delete: "stock.warehouses.actions.delete",
            view: "stock.warehouses.actions.view",
          },
        },
        translatableFields: ["name", "address"],
        translatableEnums: [],
      },
      tables: [
        {
          tableName: "warehouses",
          schema: validatorWarehouse,
          scope: "company" as TableScope,
          relations: [
            {
              key: "branch",
              sourceField: "branchId",
              schema: validatorBranch,
              tableName: "branches",
            },
            {
              key: "lastUpdatedBy",
              sourceField: "internalControl.updatedBy",
              schema: validatorUsers,
              tableName: "users",
            },
          ],
          searchFields: ["name", "code", "address"],
          hasStatusIndex: false,
          customRules: [],
        },
      ],
      readOnlyTables: [{ tableName: "branches" }],
    },
  ],
};
